import Decimal from "decimal.js";
import { Balances } from "store/global/getBalances";
import { TokenMetadata } from "store/global/getTokenMetadata";
import { Chain } from "wagmi";
import {
  Counterparty,
  TokenSelectMode,
} from "../components/Modal/instance/SelectTokenModal/TokenSelectModal";
import { MAX_SELECTABLE_TOKENS } from "../config/consts";
import { Token } from "../store/global/TokenEnum";
import { getWrappedSymbolForChain } from "./chainUtils";
import { getPriceForToken } from "./tokenMetadataUtils";
import { isNativeToken } from "./wrapUtils";

export const isTokenAvailable = (tokensMetadata: TokenMetadata | undefined, symbol: Token) =>
  tokensMetadata?.[symbol]?.availability.isAvailable ?? true;

export const hasReachedMaxLimit = (
  selectedTokens: Array<Token>,
  symbol: Token,
  tokenSelectMode: TokenSelectMode
) =>
  selectedTokens.length === MAX_SELECTABLE_TOKENS &&
  !selectedTokens.includes(symbol) &&
  tokenSelectMode !== "single";

export const sortTokensByAmount = (list: Balances, tokenMetadata?: TokenMetadata): Balances => {
  return [...list].sort((a, b) => {
    const aPrice = getPriceForToken(a.symbol, tokenMetadata);
    const bPrice = getPriceForToken(b.symbol, tokenMetadata);
    const aAmount = aPrice.mul(a.balance);
    const bAmount = bPrice.mul(b.balance);
    if (bAmount.value.gt(0) || aAmount.value.gt(0)) {
      const result = bAmount.value.sub(aAmount.value).gt(new Decimal(0)) ? 1 : -1;
      return result;
    } else {
      return a.symbol.localeCompare(b.symbol);
    }
  });
};

// NOTE: "Natives" stand for either wrapped or native tokens
// Reasons below describe the current state that prevents addition or changing of tokens
export enum TokenRowDisabledReason {
  // * tokenSelectMode === "multiple" || tokenSelectMode === "single"
  OppositePartyHasNativesAndIsNon121Trade = "OppositePartyHasNativesAndIsNon121Trade",
  IsUnavailable = "IsUnavailable",
  HasNoPrice = "HasNoPrice",

  // * tokenSelectMode === "multiple"
  SelectedTokensAlreadyIncludesNatives = "SelectedTokensAlreadyIncludesNatives",
  IsWrapTrade = "IsWrapTrade",
  ReachedMaxTokenLimit = "ReachedMaxTokenLimit",
}

export const getTokenRowDisabledReason = (
  symbol: Token,
  network: Chain,
  counterParty: Counterparty,
  takerTokensArray: Array<Token>,
  makerTokensArray: Array<Token>,
  tokensMetadata: TokenMetadata | undefined,
  selectedTokens: Array<Token>,
  hasPrice: boolean,
  tokenSelectMode: TokenSelectMode
): TokenRowDisabledReason | null => {
  const wrappedSymbolForChain = getWrappedSymbolForChain(network);
  const nativeSymbolForChain = network.nativeCurrency.symbol as Token;
  const currentSymbolIsNative = symbol === nativeSymbolForChain;
  const currentSymbolIsWrapped = symbol === wrappedSymbolForChain;
  const currentPartyTokens = counterParty === "maker" ? makerTokensArray : takerTokensArray;
  const oppositePartyTokens = counterParty === "maker" ? takerTokensArray : makerTokensArray;
  if (!isTokenAvailable(tokensMetadata, symbol)) {
    return TokenRowDisabledReason.IsUnavailable;
  }

  if (!hasPrice) {
    return TokenRowDisabledReason.HasNoPrice;
  }

  const nativeInOppositeParty = oppositePartyTokens.includes(nativeSymbolForChain);
  const wrappedInOppositeParty = oppositePartyTokens.includes(wrappedSymbolForChain);

  if (tokenSelectMode === "multiple" || tokenSelectMode === "single") {
    const isNon121Trade =
      oppositePartyTokens.length > 1 ||
      (selectedTokens.length >= 1 &&
        !(
          selectedTokens.includes(nativeSymbolForChain) ||
          selectedTokens.includes(wrappedSymbolForChain)
        ));
    const is121AndSingleChange =
      oppositePartyTokens.length === 1 &&
      selectedTokens.length === 1 &&
      tokenSelectMode === "single";
    if (
      (currentSymbolIsNative && wrappedInOppositeParty && isNon121Trade && !is121AndSingleChange) ||
      (currentSymbolIsWrapped && nativeInOppositeParty && isNon121Trade && !is121AndSingleChange)
    ) {
      return TokenRowDisabledReason.OppositePartyHasNativesAndIsNon121Trade;
    }
  }

  if (tokenSelectMode === "multiple") {
    const isNativeAndWrappedSelectedInSameParty =
      selectedTokens.includes(wrappedSymbolForChain) && currentSymbolIsNative;
    const isWrappedAndNativeSelectedInSameParty =
      selectedTokens.includes(nativeSymbolForChain) && currentSymbolIsWrapped;
    if (isNativeAndWrappedSelectedInSameParty || isWrappedAndNativeSelectedInSameParty) {
      return TokenRowDisabledReason.SelectedTokensAlreadyIncludesNatives;
    }

    if (hasReachedMaxLimit(selectedTokens, symbol, tokenSelectMode)) {
      return TokenRowDisabledReason.ReachedMaxTokenLimit;
    }

    const isWrappingFlow =
      (currentPartyTokens.length === 1 &&
        currentPartyTokens[0] === nativeSymbolForChain &&
        oppositePartyTokens.length === 1 &&
        oppositePartyTokens[0] === wrappedSymbolForChain) ||
      (currentPartyTokens.length === 1 &&
        currentPartyTokens[0] === wrappedSymbolForChain &&
        oppositePartyTokens.length === 1 &&
        oppositePartyTokens[0] === nativeSymbolForChain) ||
      (symbol !== nativeSymbolForChain &&
        selectedTokens.length === 1 &&
        selectedTokens[0] === nativeSymbolForChain &&
        oppositePartyTokens.length === 1 &&
        oppositePartyTokens[0] === wrappedSymbolForChain) ||
      (symbol !== wrappedSymbolForChain &&
        selectedTokens.length === 1 &&
        selectedTokens[0] === wrappedSymbolForChain &&
        oppositePartyTokens.length === 1 &&
        oppositePartyTokens[0] === nativeSymbolForChain);

    if (isWrappingFlow) {
      return TokenRowDisabledReason.IsWrapTrade;
    }
  }

  return null;
};

export const getIsWrapOrUnwrap = (
  takerTokens: Array<Token>,
  makerTokens: Array<Token>,
  chain: Chain
) => {
  if (takerTokens.length !== 1 || makerTokens.length !== 1) {
    return false;
  }

  if (
    (isNativeToken(takerTokens[0], chain) && makerTokens[0] === getWrappedSymbolForChain(chain)) ||
    (isNativeToken(makerTokens[0], chain) && takerTokens[0] === getWrappedSymbolForChain(chain))
  ) {
    return true;
  }
  return false;
};
